import "semantic-ui-css/semantic.min.css";
import "./styles/styles.scss";
import "./styles/kendo.scss";
import "@itm21st/faceted-filter/dist/style.css";
import "react-toastify/dist/ReactToastify.css";

import React, { Suspense, lazy } from "react";
import { Route, Routes } from "react-router-dom";

import AuthError from "./components/AuthError";
import CatchAllRedirect from "./components/CatchAllRedirect";
import InboundContextProvider from "./features/inbound/InboundContextProvider";
import Layout from "./components/Layout";
import PolicyCopy from "./features/policy-copy/PolicyCopy";
import PolicyQueueContextProvider from "./features/policy-queue/PolicyQueueContextProvider";
import PrivateRoute from "./components/PrivateRoute";
import ProductLibraryContextProvider from "./features/product-library/ProductLibraryContextProvider";
import { RefreshQueueContextProvider } from "./features/meds-le-refresh/RefreshQueueContextProvider";
import { Role } from "./types";
import SimpleLoader from "./components/SimpleLoader";
import TradingQueueContextProvider from "./features/trading/trading-queue/TradingQueueContextProvider";
import { useAuth0 } from "@auth0/auth0-react";

const AddPortfolio = lazy(() => import("./features/portfolio/AddPortfolio"));
const CreateTradingCase = lazy(() => import("./features/trading/create-trading-case/CreateTradingCaseContextProvider"));
const BillingExport = lazy(() => import("./features/billing-export/BillingExport"));
const Carrier = lazy(() => import("./features/carrier/CarrierContextProvider"));
const CarrierRequests = lazy(() => import("./features/carrier-requests/CarrierRequestContextProvider"));
const CaseDetail = lazy(() => import("./features/trading/trading-queue/CaseDetail"));
const ClientDetails = lazy(() => import("./features/client/ClientPage"));
const ClientPolicyUploadQueue = lazy(() => import("./features/client-policy-upload-queue/ClientPolicyUploadQueue"));
const ClientSearch = lazy(() => import("./features/client-search/ClientSearch"));
const ComparisonGenerator = lazy(() => import("./features/comparison-generator/ComparisonGenerator"));
const ContactSearch = lazy(() => import("./features/contact-search/ContactSearch"));
const CreateClient = lazy(() => import("./features/create-client/CreateClient"));
const DeathClaims = lazy(() => import("./features/death-claims-queue/DeathClaimsQueue"));
const DocumentManagement = lazy(() => import("./features/document-management/DocumentManagementContextProvider"));
const Decrypt = lazy(() => import("./features/encryption-services/Decrypt"));
const EntrustQueue = lazy(() => import("./features/entrust-queue/EntrustQueue"));
const EntrustLifeSettlementCase = lazy(() => import("./features/entrust-queue/EntrustLifeSettlementCase"));
const EntrustNewBusinessCase = lazy(() => import("./features/entrust-queue/EntrustNewBusinessCase"));
const ExternalLEQueue = lazy(() => import("./features/external-le-queue/ExternalLEQueue"));
const FaxInbox = lazy(() => import("./features/fax-inbox/FaxInbox"));
const GraceQueue = lazy(() => import("./features/grace-queue/GraceQueue"));
const Illustration = lazy(() => import("./features/illustration/IllustrationContextProvider"));
const Inbound = lazy(() => import("./features/inbound/InboundQueue"));
const InboundFileValidation = lazy(() => import("./features/inbound/InboundFileValidation/InboundFileValidation"));
const InboundIllustrationReview = lazy(() => import("./features/inbound/InboundIllustration/InboundIllustrationReview"));
const Insured = lazy(() => import("./features/insured/InsuredContextProvider"));
const InsuredSearch = lazy(() => import("./features/insured-search/InsuredSearch"));
const InsuredsNearMaturity = lazy(() => import("./features/insureds-near-maturity/InsuredsNearMaturity"));
const LexservUser = lazy(() => import("./features/user/LexservUser"));
const LongevityAnalytics = lazy(() => import("./features/longevity-analytics/LongevityAnalytics"));
const LongevityAnalyticsUser = lazy(() => import("./features/user/LongevityAnalyticsUser"));
const LongevityAnalyticsUserSearch = lazy(() => import("./features/user-search/LongevityAnalyticsUserSearch"));
const MachineUser = lazy(() => import("./features/machine-user/MachineUser"));
const MailMerge = lazy(() => import("./features/mail-merge/MailMergeContextProvider"));
const MassUpload = lazy(() => import("./features/bulk-premium-upload/MassUpload"));
const MassUploadDetails = lazy(() => import("./features/bulk-premium-upload/MassUploadDetails"));
const MedRefreshQueue = lazy(() => import("./features/meds-le-refresh/MedsRefreshQueue"));
const MedRecOrder = lazy(() => import("./features/medrec-order/MedRecOrderContextProvider"));
const MedrecQueue = lazy(() => import("./features/medrec-queue/MedrecQueue"));
const OptQueue = lazy(() => import("./features/policy-opt-queue/PolicyOptQueue"));
const PaymentHolds = lazy(() => import("./features/payment-holds/PaymentHolds"));
const Policy = lazy(() => import("./features/policy/PolicyContextProvider"));
const PolicyDetail = lazy(() => import("./features/policy-queue/PolicyDetail"));
const PolicyQueue = lazy(() => import("./features/policy-queue/PolicyQueue"));
const PolicySearch = lazy(() => import("./features/policy-search/PolicySearch"));
const PremiumConfirmQueue = lazy(() => import("./features/premium-confirm-queue/PremiumConfirmQueue"));
const PolicyValuation = lazy(() => import("./features/policy-valuation/PolicyValuationContextProvider"));
const Portfolio = lazy(() => import("./features/portfolio/Portfolio"));
const PortfolioSearch = lazy(() => import("./features/portfolio-search/PortfolioSearch"));
const PortfolioUpload = lazy(() => import("./features/portfolio-upload/PortfolioUploadContextProvider"));
const ProductLibrary = lazy(() => import("./features/product-library/ProductLibrary"));
const ProjectionManagement = lazy(() => import("./features/projection-management/ProjectionManagementContextProvider"));
const ReportGenerator = lazy(() => import("./features/report-generator/ReportGeneratorContextProvider"));
const SecureFileTransfer = lazy(() => import("./features/secure-file-transfer/SecureFileTransfer"));
const ServiceCenter = lazy(() => import("./features/service-center/ServiceCenterDetail/ServiceCenterContextProvider"));
const ServiceCenterGrid = lazy(() => import("./features/service-center/ServiceCenterGrid/ServiceCenterGrid"));
const SpecialAuthFiles = lazy(() => import("./features/special-auth-files/SpecialAuthFiles"));
const SpecialProjectOpt = lazy(() => import("./features/special-project-opt/SpecialProjectOpt"));
const TermWholeLife = lazy(() => import("./features/term-whole-life-queue/TermWholeLifeQueue"));
const TrackingPage = lazy(() => import("./features/tracking-queue/TrackingPage/TrackingPage"));
const TrackingQueue = lazy(() => import("./features/tracking-queue/TrackingQueue"));
const TradingCompanyList = lazy(() => import("./features/trading/trading-company/TradingCompanyList"));
const TradingCompany = lazy(() => import("./features/trading/trading-company/TradingCompanyDetail"));
const TradingQueue = lazy(() => import("./features/trading/trading-queue/TradingQueue"));
const UserRoleAdmin = lazy(() => import("./features/user-admin/UserRoleAdmin"));
const UserSearch = lazy(() => import("./features/user-search/LexservUserSearch"));
const ValidationQueue = lazy(() => import("./features/validation-queue"));
const WireInstructions = lazy(() => import("./features/wire-instructions/WireInstructions"));
const WireTemplateEditor = lazy(() => import("./features/wire-instructions/wire-template-editor/WireTemplateEditor"));

const App: React.FC = () => {
    const { isLoading, error } = useAuth0();
    if (isLoading) {
        return <SimpleLoader />;
    }

    if (error) {
        return <AuthError error={error} />;
    }

    return (
        <Suspense fallback={<p>Loading...</p>}>
            <Routes>
                <Route path="/" element={<PrivateRoute component={Layout} />}>
                    <Route path="/add-portfolio" element={<PrivateRoute component={AddPortfolio} allowIfHasRole={Role.ServicingUser} />} />
                    <Route
                        path="/billing-export"
                        element={<PrivateRoute component={BillingExport} allowIfHasAnyRole={[Role.Accounting, Role.AccountingAdmin]} />}
                    />
                    <Route
                        path="/create-trading-case/:caseID?"
                        element={<PrivateRoute component={CreateTradingCase} allowIfHasRole={Role.ServicingTrading} />}
                    />
                    <Route path="/carrier/:carrierID?" element={<PrivateRoute component={Carrier} allowIfHasRole={Role.ServicingUser} />} />
                    <Route
                        path="/carrier-requests"
                        element={<PrivateRoute component={CarrierRequests} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/comparison-generator/:policyID?"
                        element={<PrivateRoute component={ComparisonGenerator} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route path="/contact" element={<PrivateRoute component={ContactSearch} allowIfHasRole={Role.ServicingUser} />} />
                    <Route
                        path="/client/:clientID"
                        element={<PrivateRoute component={ClientDetails} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route path="/client" element={<PrivateRoute component={ClientSearch} allowIfHasRole={Role.ServicingUser} />} />
                    <Route
                        path="/create-client"
                        element={<PrivateRoute component={CreateClient} allowIfHasAnyRole={[Role.GenericAdmin, Role.UWSupportSr]} />}
                    />
                    <Route path="/death-claims" element={<PrivateRoute component={DeathClaims} allowIfHasRole={Role.ServicingUser} />} />
                    <Route
                        path="/document-management"
                        element={<PrivateRoute component={DocumentManagement} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route path="/decrypt" element={<PrivateRoute component={Decrypt} allowIfHasRole={Role.ServicingUser} />} />
                    <Route
                        path="/entrust-queue/ls/:id"
                        element={<PrivateRoute component={EntrustLifeSettlementCase} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/entrust-queue/nb/:id"
                        element={<PrivateRoute component={EntrustNewBusinessCase} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route path="/entrust-queue" element={<PrivateRoute component={EntrustQueue} allowIfHasRole={Role.ServicingUser} />} />
                    <Route
                        path="/external-le-queue"
                        element={<PrivateRoute component={ExternalLEQueue} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route path="/fax-inbox" element={<PrivateRoute component={FaxInbox} allowIfHasRole={Role.ServicingUser} />} />
                    <Route
                        path="/client-policy-upload-queue"
                        element={<PrivateRoute component={ClientPolicyUploadQueue} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route path="/grace-queue" element={<PrivateRoute component={GraceQueue} allowIfHasRole={Role.ServicingUser} />} />
                    <Route
                        path="/illustration/:illustrationID"
                        element={<PrivateRoute component={Illustration} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/illustration/policyID/:policyId"
                        element={<PrivateRoute component={Illustration} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/illustration/pricingPolicyID/:id"
                        element={<PrivateRoute component={Illustration} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/inbound/:fileID"
                        element={
                            <InboundContextProvider>
                                <PrivateRoute component={InboundFileValidation} allowIfHasRole={Role.ServicingUser} />
                            </InboundContextProvider>
                        }
                    />
                    <Route
                        path="/inbound/illustration"
                        element={
                            <InboundContextProvider>
                                <PrivateRoute component={InboundIllustrationReview} allowIfHasRole={Role.ServicingUser} />
                            </InboundContextProvider>
                        }
                    />
                    <Route
                        path="/inbound"
                        element={
                            <InboundContextProvider>
                                <PrivateRoute component={Inbound} allowIfHasRole={Role.ServicingUser} />
                            </InboundContextProvider>
                        }
                    />
                    <Route path="/insured/:insuredID" element={<PrivateRoute component={Insured} allowIfHasRole={Role.ServicingUser} />} />
                    <Route path="/insured" element={<PrivateRoute component={InsuredSearch} allowIfHasRole={Role.ServicingUser} />} />
                    <Route
                        path="/insureds-near-maturity"
                        element={<PrivateRoute component={InsuredsNearMaturity} allowIfHasRole={Role.ServicingPolicyManager} />}
                    />
                    <Route path="/lexserv-user" element={<PrivateRoute component={UserSearch} allowIfHasRole={Role.ServicingUser} />} />
                    <Route
                        path="/lexserv-user/:authID"
                        element={<PrivateRoute component={LexservUser} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/longevity-Analytics"
                        element={<PrivateRoute component={LongevityAnalytics} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/longevity-analytics-user"
                        element={<PrivateRoute component={LongevityAnalyticsUserSearch} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/longevity-analytics-user/:authID"
                        element={<PrivateRoute component={LongevityAnalyticsUser} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/machine-user"
                        element={
                            <PrivateRoute component={MachineUser} allowIfHasAnyRole={[Role.GenericAdmin, Role.ServicingPolicyManager]} />
                        }
                    />
                    <Route path="/mail-merge" element={<PrivateRoute component={MailMerge} allowIfHasRole={Role.ServicingUser} />} />
                    <Route path="/mass-upload" element={<PrivateRoute component={MassUpload} allowIfHasRole={Role.ServicingUser} />} />
                    <Route
                        path="/mass-upload/:sessionID"
                        element={<PrivateRoute component={MassUploadDetails} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/refresh-queue"
                        element={
                            <RefreshQueueContextProvider>
                                <PrivateRoute component={MedRefreshQueue} allowIfHasRole={Role.ServicingUser} />
                            </RefreshQueueContextProvider>
                        }
                    />
                    <Route
                        path="/medrec-order/:mrid?"
                        element={<PrivateRoute component={MedRecOrder} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route path="/medrec-queue" element={<PrivateRoute component={MedrecQueue} allowIfHasRole={Role.ServicingUser} />} />
                    <Route path="/policy/:policyID" element={<PrivateRoute component={Policy} allowIfHasRole={Role.ServicingUser} />} />
                    <Route
                        path="/policy/:policyID/grace-event/:graceID"
                        element={<PrivateRoute component={Policy} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route path="/policy" element={<PrivateRoute component={PolicySearch} allowIfHasRole={Role.ServicingUser} />} />
                    <Route path="/policy-copy" element={<PrivateRoute component={PolicyCopy} allowIfHasRole={Role.ServicingAdmin} />} />
                    <Route path="/policy-opt-queue" element={<PrivateRoute component={OptQueue} allowIfHasRole={Role.ServicingUser} />} />
                    <Route
                        path="/policy-queue/:policyID"
                        element={
                            <PolicyQueueContextProvider>
                                <PrivateRoute component={PolicyDetail} allowIfHasRole={Role.ServicingUser} />
                            </PolicyQueueContextProvider>
                        }
                    />
                    <Route
                        path="/policy-queue"
                        element={
                            <PolicyQueueContextProvider>
                                <PrivateRoute component={PolicyQueue} allowIfHasRole={Role.ServicingUser} />
                            </PolicyQueueContextProvider>
                        }
                    />
                    <Route
                        path="/policy-valuation"
                        element={<PrivateRoute component={PolicyValuation} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/portfolio/:portfolioID"
                        element={<PrivateRoute component={Portfolio} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route path="/portfolio" element={<PrivateRoute component={PortfolioSearch} allowIfHasRole={Role.ServicingUser} />} />
                    <Route
                        path="/portfolio-upload"
                        element={<PrivateRoute component={PortfolioUpload} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route path="/payment-holds" element={<PrivateRoute component={PaymentHolds} allowIfHasRole={Role.ServicingUser} />} />
                    <Route
                        path="/premium-confirm-queue"
                        element={<PrivateRoute component={PremiumConfirmQueue} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/product-library/:productLibraryID"
                        element={
                            <ProductLibraryContextProvider>
                                <PrivateRoute component={ProductLibrary} allowIfHasRole={Role.ServicingUser} />
                            </ProductLibraryContextProvider>
                        }
                    />
                    <Route
                        path="/product-library"
                        element={
                            <ProductLibraryContextProvider>
                                <PrivateRoute component={ProductLibrary} allowIfHasRole={Role.ServicingUser} />
                            </ProductLibraryContextProvider>
                        }
                    />

                    <Route
                        path="/projection-management/:policyID"
                        element={<PrivateRoute component={ProjectionManagement} allowIfHasRole={Role.ServicingUser} />}
                    />

                    <Route
                        path="/report-generator"
                        element={<PrivateRoute component={ReportGenerator} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/secure-file-transfer"
                        element={<PrivateRoute component={SecureFileTransfer} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/service-center"
                        element={<PrivateRoute component={ServiceCenterGrid} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/service-center/:serviceCenterID"
                        element={<PrivateRoute component={ServiceCenter} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/special-auth-files"
                        element={<PrivateRoute component={SpecialAuthFiles} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/special-project-opt"
                        element={<PrivateRoute component={SpecialProjectOpt} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/term-whole-life"
                        element={<PrivateRoute component={TermWholeLife} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/tracking-queue"
                        element={<PrivateRoute component={TrackingQueue} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/tracking/:insuredID"
                        element={<PrivateRoute component={TrackingPage} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/trading-company"
                        element={<PrivateRoute component={TradingCompanyList} allowIfHasRole={Role.ServicingTrading} />}
                    />
                    <Route
                        path="/trading-company/:companyID"
                        element={<PrivateRoute component={TradingCompany} allowIfHasRole={Role.ServicingTrading} />}
                    />
                    <Route
                        path="/trading-queue/:caseID"
                        element={
                            <TradingQueueContextProvider>
                                <PrivateRoute component={CaseDetail} allowIfHasRole={Role.ServicingTrading} />
                            </TradingQueueContextProvider>
                        }
                    />
                    <Route
                        path="/trading-queue"
                        element={
                            <TradingQueueContextProvider>
                                <PrivateRoute component={TradingQueue} allowIfHasRole={Role.ServicingTrading} />
                            </TradingQueueContextProvider>
                        }
                    />
                    <Route path="/user-admin" element={<PrivateRoute component={UserRoleAdmin} allowIfHasRole={Role.GenericAdmin} />} />

                    <Route
                        path="/validation-queue"
                        element={<PrivateRoute component={ValidationQueue} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/wire-instructions"
                        element={<PrivateRoute component={WireInstructions} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route
                        path="/wire-template-editor"
                        element={<PrivateRoute component={WireTemplateEditor} allowIfHasRole={Role.ServicingUser} />}
                    />
                    <Route path="/" element={<CatchAllRedirect />} />
                    <Route path="*" element={<CatchAllRedirect />} />
                </Route>
            </Routes>
        </Suspense>
    );
};

export default App;
